import React, { useState, useEffect, useRef } from 'react';

const API_BASE = "/api/art_saron.php";

const SET_MAP = {
    'WHIZBANGS_WORKSHOP': { icon: 'TOYIcon', label: "Whizbang's Workshop" },
    'ISLAND_VACATION': { icon: 'PRIcon', label: "Perils in Paradise" },
    'SPACE': { icon: 'GDBIcon', label: "Great Dark Beyond" },
    'EMERALD_DREAM': { icon: 'EDRIcon', label: "Emerald Dream" },
    'THE_LOST_CITY': { icon: 'TLCIcon', label: "Lost City of Un'Goro" },
    'TIME_TRAVEL': { icon: 'ATTIcon', label: "Across the Timeways" },
    'CATACLYSM': { icon: 'CATAIcon', label: "Cataclysm" },
    'ESCAPEFROM_VIOLET_HOLD': { icon: 'JAILIcon', label: "Escape from Violet Hold" }
};

const ZOOM_LEVELS = { 
    0: '2100%', 
    1: '1100%',  
    2: '600%',  
    3: '160%',  
    4: '20%'    
};

const parseZoomToScale = (zoomVal) => {
    if (zoomVal === undefined || zoomVal === null) return 1.0;
    const numeric = parseFloat(String(zoomVal).replace('%', ''));
    if (isNaN(numeric)) return 1.0;
    return 1.0 + (numeric / 100);
};

export default function ArtSaron() {
    const [mode, setMode] = useState('daily'); 
    const [gameState, setGameState] = useState(null);
    const [cardList, setCardList] = useState([]);
    const [guessInput, setGuessInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [activeSugIndex, setActiveSugIndex] = useState(-1);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    
    const wrapperRef = useRef(null);

    const instanceId = gameState?.instance_id || '1';
    const proxyImageUrl = `${API_BASE}?action=image&mode=${mode}&v=${instanceId}`;

    useEffect(() => {
        fetchCardNames();
        loadGame(mode);
    }, [mode]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setSuggestions([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setImageLoaded(false);
    }, [proxyImageUrl]);

    const fetchCardNames = async () => {
        try {
            const res = await fetch(`${API_BASE}?action=get_card_list`);
            const data = await res.json();
            setCardList(data);
        } catch (err) {
            console.error("Failed to load cards list", err);
        }
    };

    const loadGame = async (currentMode) => {
        setLoading(true);
        setErrorMessage('');
        setGuessInput('');
        setSuggestions([]);
        setImageLoaded(false);
        try {
            const res = await fetch(`${API_BASE}?action=init&mode=${currentMode}`);
            const data = await res.json();
            setGameState(data);
        } catch (err) {
            setErrorMessage("Error connecting to server.");
        } finally {
            setLoading(false);
        }
    };

    const handleGuessSubmit = async (nameToGuess) => {
        const query = nameToGuess || guessInput;
        if (!query) return;

        setLoading(true);
        setErrorMessage('');
        try {
            const res = await fetch(`${API_BASE}?action=guess&mode=${mode}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ guess_name: query })
            });
            const data = await res.json();
            if (data.error) {
                setErrorMessage(data.error);
            } else {
                setGameState(data);
                setGuessInput('');
                setSuggestions([]);
            }
        } catch (err) {
            setErrorMessage("Failed to send guess.");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async () => {
        if (mode !== 'practice') return;
        setLoading(true);
        setImageLoaded(false);
        try {
            const res = await fetch(`${API_BASE}?action=reset&mode=practice`, { method: 'POST' });
            const data = await res.json();
            setGameState(data);
            setGuessInput('');
            setSuggestions([]);
        } catch (err) {
            setErrorMessage("Reset failed.");
            setImageLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setGuessInput(value);
        if (!value) {
            setSuggestions([]);
            return;
        }

        const filtered = cardList
            .filter(name => name.toLowerCase().includes(value.toLowerCase()))
            .slice(0, 7);
        setSuggestions(filtered);
        setActiveSugIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (e.keyCode === 40) { 
            e.preventDefault();
            setActiveSugIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
        } else if (e.keyCode === 38) { 
            e.preventDefault();
            setActiveSugIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
        } else if (e.keyCode === 13) { 
            e.preventDefault();
            if (activeSugIndex > -1 && suggestions[activeSugIndex]) {
                const selected = suggestions[activeSugIndex];
                setGuessInput(selected);
                setSuggestions([]);
                handleGuessSubmit(selected);
            } else {
                handleGuessSubmit();
            }
        }
    };

    const getClassIconUrl = (classStr) => {
        const classMap = {
            'DEMONHUNTER': 'demonhunter',
            'DEATHKNIGHT': 'deathknight',
            'NEUTRAL': 'neutral'
        };
        const key = classStr?.toUpperCase() || '';
        const slug = classMap[key] || key.toLowerCase();
        return `https://www.hearthcards.net/images/class-icons/${slug}.png`;
    };

    const getRarityIconUrl = (rarityStr) => {
        const key = rarityStr?.toUpperCase() || '';
        if (key === 'FREE' || key === 'NONE') return null;
        return `https://www.hearthcards.net/images/rarity-icons/${key.toLowerCase()}.webp`;
    };

    const getSetIconUrl = (setKey) => {
        const iconId = SET_MAP[setKey]?.icon;
        return iconId ? `https://www.hearthcards.net/images/selectorEmblems/${iconId}.png` : null;
    };

    const getTypeIconUrl = (typeStr) => {
        const key = typeStr?.toUpperCase() || '';
        const typeMap = {
            'MINION': 'icon-minion',
            'SPELL': 'icon-spell',
            'WEAPON': 'icon-weapon',
            'LOCATION': 'location-icon',
            'HERO': 'hero-icon (1)'
        };
        const slug = typeMap[key] || 'spell';
        return `/images/mobile/${slug}.webp`;
    };

    if (!gameState) {
        return (
            <div className="text-center p-5 text-dark w-100">
                <div className="spinner-border text-secondary" role="status"></div>
                <div className="mt-2 fw-medium">Loading the data...</div>
            </div>
        );
    }

    const currentAttempt = gameState.attempts.length;
    const scale = parseZoomToScale(ZOOM_LEVELS[currentAttempt]);
    const isPlaying = gameState.status === 'playing';

    return (
        <div className="artsaron-fullscreen-wrapper w-100">
            <style>{`
                .artsaron-fullscreen-wrapper {
                    min-height: 100vh;
                    color: #3e2723; /* Dark brown readable text */
                    width: 100%;
                }
                .artsaron-inner {
                    width: 100%;
                    max-width: 100%;
                    padding: 12px;
                    box-sizing: border-box;
                }
                
                .artsaron-viewport {
                    width: 60vw;
                    height: 60vw;
                    max-width: 200px;
                    max-height: 200px;
                    margin: 0 auto 10px auto;
                    border: 4px solid #8d6e63; /* Medium brown frame */
                    border-radius: 8px;
                    box-shadow: inset 0 0 8px rgba(0,0,0,0.3), 0 3px 6px rgba(0,0,0,0.15);
                }

                @media (min-width: 768px) {
                    .artsaron-inner {
                        max-width: 780px;

                        margin: 20px auto;
                        padding: 24px;
                    }
                }

                .italic {
                    font-style: italic;
                }
                .clue-icon {
                    height: 24px;
                    width: auto;
                    vertical-align: middle;
                }
                .tooltip-cell {
                    cursor: help;
                }
                
                .table-artsaron-wrapper {
                    width: 100%;
                    border: 1px solid #d7ccc8;
                    border-radius: 6px;
                    overflow: hidden;
                    margin-top: 15px;
                    border: 0px solid transparent;
    border-image-source: url(https://www.hearthcards.net/images/contest/border-test9.webp);
    border-image-slice: 128;
    border-image-width: 60px;
    border-image-outset: 0;
    border-image-repeat: stretch;
    border-radius: 25px;
    padding: 20px;
                }
                .table-artsaron {

                    color: #3e2723 !important;
                    margin-bottom: 0 !important;
                    table-layout: fixed;
                    width: 100%;
                }
                .table-artsaron th {
                    background-color: #efebe900 !important;
                    color: #5d4037 !important;
                    font-size: 11px;
                    border-bottom: 2px solid #d7ccc8 !important;
                }
                .table-artsaron td {
                    font-size: 11px;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    border-bottom: 1px solid #efebe9 !important;
                }
                .match-cell {
                    background-color: #2e7d32 !important;
                    color: #ffffff !important;
                }
                .mismatch-cell {
                    background-color: #c62828 !important;
                    color: #ffffff !important;
                }
                .arrow-up::after { content: " ⬆"; }
                .arrow-down::after { content: " ⬇"; }
            `}</style>

            <div className="artsaron-inner">
                {/* 1. HEADER SECTION */}
                <div className="text-center mb-2">
                    
                    <div class="ribbon-container d-md-inline-flex"><div class="ribbon-text">Art-Saron</div></div>
                    <p className="small text-muted mb-2 italic">Identify the card from a cropped fragment of its art.</p>
                    
                    {/* Switcher */}
                    <div className="btn-group w-100" style={{ maxWidth: '350px' }} role="group">
                        <button 
                            type="button" 
                            className={`btn btn-sm ${mode === 'daily' ? 'btn-brown text-white' : 'btn-outline-brown'}`}
                            style={{
                                backgroundColor: mode === 'daily' ? '#000000' : 'transparent',
                                color: mode === 'daily' ? '#fff' : '#5d4037',
                                borderColor: '#000000'
                            }}
                            onClick={() => setMode('daily')}
                        >
                            Daily Challenge
                        </button>
                        <button 
                            type="button" 
                            className={`btn btn-sm ${mode === 'practice' ? 'btn-brown text-white' : 'btn-outline-brown'}`}
                            style={{
                                backgroundColor: mode === 'practice' ? '#000000' : 'transparent',
                                color: mode === 'practice' ? '#fff' : '#000000',
                                borderColor: '#5d4037'
                            }}
                            onClick={() => setMode('practice')}
                        >
                            Practice Mode
                        </button>
                    </div>
                </div>

                {/* 2. VIEWPORT */}
                <div className="text-center py-4">
                    {isPlaying ? (
                        <div className="artsaron-viewport" style={{ position: 'relative', overflow: 'hidden', backgroundColor: '#efebe9' }}>
                            {!imageLoaded && (
                                <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 10 }}>
                                    <div className="spinner-border text-secondary spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            )}
                            <img 
                                src={proxyImageUrl} 
                                alt="cropped fragment" 
                                onLoad={() => setImageLoaded(true)}
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    maxWidth: 'none',
                                    maxHeight: 'none',
                                    transformOrigin: 'center center',
                                    transform: `scale(${scale})`,
                                    transition: 'transform 0.5s ease, opacity 0.4s ease-in-out',
                                    opacity: imageLoaded ? 1 : 0,
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                    WebkitUserDrag: 'none'
                                }}
                            />
                        </div>
                    ) : (
                        gameState.target_card && (
                            <img 
                                src={`https://art.hearthstonejson.com/v1/render/latest/enUS/512x/${gameState.target_card.card_id}.png`} 
                                alt={gameState.target_card.name} 
                                className="img-fluid d-block mx-auto"
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                                style={{ maxWidth: '300px', marginTop: '-20px', marginBottom: '-50px', userSelect: 'none', filter: 'drop-shadow(2px 4px 6px black)' }}
                            />
                        )
                    )}
                </div>

                {/* Completion Details */}
                {!isPlaying && (
                    <div className="text-center mb-2">
                        {gameState.status === 'won' ? (
                            <h4 className="text-success fw-bold mb-1">Victory!</h4>
                        ) : (
                            <h4 className="text-danger fw-bold mb-1">Defeat!</h4>
                        )}
                        <p className="small mb-2" style={{ color: '#3e2723' }}>Solution: <strong>{gameState.target_card?.name}</strong></p>
                        
                        {mode === 'practice' && (
                            <button 
                                className="btn btn-sm text-white px-3 fw-bold py-3 px-3 my-1 button-create" 
                                style={{ backgroundColor: '#5d4037' }}
                                onClick={handleReset}
                            >
                                Next Match
                            </button>
                        )}
                    </div>
                )}

                {/* 3. GUESS INPUT FIELD */}
                {isPlaying && (
                    <div className="text-center mb-2">
                        <p className="small text-muted mb-1" style={{ fontSize: '12px' }}>Attempt {currentAttempt + 1} of 5</p>
                        <div className="d-flex justify-content-center mx-auto" style={{ maxWidth: '350px' }}>
                            <div className="position-relative flex-grow-1" ref={wrapperRef}>
                                <input 
                                    type="text"
                                    className="form-control form-control-sm bg-white text-dark border-secondary"
                                    placeholder="Enter card name..."
                                    value={guessInput}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    disabled={loading}
                                />
                                {suggestions.length > 0 && (
                                    <div className="list-group position-absolute w-100 shadow-lg text-start" style={{ zIndex: 1000, top: '100%' }}>
                                        {suggestions.map((name, idx) => (
                                            <button 
                                                key={name}
                                                type="button"
                                                className={`list-group-item list-group-item-action list-group-item-sm bg-white text-dark border-light ${idx === activeSugIndex ? 'active bg-warning text-dark' : ''}`}
                                                onClick={() => {
                                                    setGuessInput(name);
                                                    setSuggestions([]);
                                                    handleGuessSubmit(name);
                                                }}
                                            >
                                                {name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button 
                                className="btn btn-sm text-white ms-2 fw-bold" 
                                style={{ backgroundColor: '#5d4037' }}
                                onClick={() => handleGuessSubmit()} 
                                disabled={loading}
                            >
                                Guess
                            </button>
                        </div>
                    </div>
                )}

                {errorMessage && (
                    <div className="alert alert-danger py-1 px-3 mx-auto text-center mb-2" style={{ maxWidth: '300px', fontSize: '13px' }}>
                        {errorMessage}
                    </div>
                )}

                {/* 4. CLUES / FEEDBACK TABLE */}
                {gameState.attempts.length > 0 && (
                    <div className="table-artsaron-wrapper">
                        <table className="table table-dark table-artsaron align-middle text-center mb-0">
                            <colgroup>
                                <col style={{ width: '32%' }} />
                                <col style={{ width: '13%' }} />
                                <col style={{ width: '15%' }} />
                                <col style={{ width: '13%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '13%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th scope="col" className="text-start ps-2">Guess</th>
                                    <th scope="col">Class</th>
                                    <th scope="col">Set</th>
                                    <th scope="col">Cost</th>
                                    <th scope="col">Type</th>
                                    <th scope="col">Rarity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...gameState.attempts].reverse().map((att, index) => {
                                    const setInfo = SET_MAP[att.set] || { label: att.set };
                                    const setIcon = getSetIconUrl(att.set);
                                    const rarityIcon = getRarityIconUrl(att.rarity);
                                    const classIcon = getClassIconUrl(att.class);
                                    const typeIcon = getTypeIconUrl(att.type);

                                    // Retain high contrast dark overlay for the card thumbnail image
                                    const guessCellStyle = att.card_id ? {
                                        backgroundImage: `linear-gradient(90deg, rgb(0, 0, 0) 15%, rgba(0, 0, 0, 0.45) 70%, rgba(0, 0, 0, 0.1) 100%), url(https://art.hearthstonejson.com/v1/tiles/${att.card_id}.webp)`,
                                        backgroundPosition: 'right center',
                                        backgroundSize: 'cover',
                                        backgroundRepeat: 'no-repeat',
                                        minHeight: '48px',
                                        height: '48px',
                                        boxShadow: 'rgb(0, 0, 0) 0px 3px 5px 1px inset, rgb(0, 0, 0) 0px 0px 3px',
                                        color: '#ffffff',
                                    } : {
                                        color: '#3e2723'
                                    };

                                    return (
                                        <tr key={index}>
                                            <td 
                                                className="text-start fw-bold ps-2 align-middle" 
                                                style={{ 
                                                    fontSize: '10px', 
                                                    lineHeight: '1.2',
                                                    ...guessCellStyle 
                                                }}
                                            >
                                                {att.name}
                                            </td>
                                            
                                            {/* Class Column */}
                                            <td 
                                                className={`tooltip-cell ${att.class_match ? 'match-cell' : 'mismatch-cell'}`}
                                                title={att.class_match ? "Correct Class" : `Incorrect Class. Target is not ${att.class}`}
                                            >
                                                <img src={classIcon} alt={att.class} className="clue-icon d-block mx-auto mb-1" onError={(e) => e.target.style.display='none'} />
                                                <span className="d-none d-md-block" style={{ fontSize: '9px' }}>
                                                    {att.class === 'DEMONHUNTER' ? 'DH' : (att.class === 'DEATHKNIGHT' ? 'DK' : att.class)}
                                                </span>
                                            </td>

                                            {/* Set Column */}
                                            <td 
                                                className={`tooltip-cell ${att.set_match ? 'match-cell' : 'mismatch-cell'}`}
                                                title={att.set_match ? "Correct Set" : `Incorrect Set. Target is not from ${setInfo.label}`}
                                            >
                                                {setIcon && <img src={setIcon} alt={setInfo.label} className="clue-icon d-block mx-auto mb-1" />}
                                                <span className="d-none d-md-block" style={{ fontSize: '9px', lineHeight: '1.1' }}>{setInfo.label}</span>
                                            </td>

                                            {/* Cost Column */}
                                            <td 
                                                className={`tooltip-cell ${att.cost_match ? 'match-cell' : 'mismatch-cell'}`}
                                                title={att.cost_match ? "Correct Cost" : `The correct cost is ${att.cost_diff} than ${att.cost}`}
                                                style={{ fontSize: '11px' }}
                                            >
                                                <span className="fw-bold">{att.cost}</span>
                                                {!att.cost_match && (
                                                    <span className={`small ${att.cost_diff === 'higher' ? 'arrow-up' : 'arrow-down'}`} />
                                                )}
                                            </td>

                                            {/* Type Column */}
                                            <td 
                                                className={`tooltip-cell ${att.type_match ? 'match-cell' : 'mismatch-cell'}`}
                                                title={att.type_match ? "Correct Type" : `Incorrect Type. Target is not a ${att.type}`}
                                            >
                                                <img src={typeIcon} alt={att.type} className="clue-icon d-block mx-auto mb-1" onError={(e) => e.target.style.display='none'} />
                                                <span className="d-none d-md-block" style={{ fontSize: '10px' }}>{att.type}</span>
                                            </td>

                                            {/* Rarity Column */}
                                            <td 
                                                className={`tooltip-cell ${att.rarity_match ? 'match-cell' : 'mismatch-cell'}`}
                                                title={att.rarity_match ? "Correct Rarity" : `Incorrect Rarity. Target is not ${att.rarity}`}
                                            >
                                                {rarityIcon && <img src={rarityIcon} alt={att.rarity} className="clue-icon d-block mx-auto mb-1" />}
                                                <span className="d-none d-md-block" style={{ fontSize: '9px' }}>{att.rarity}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Practice Mode Actions */}
                {mode === 'practice' && isPlaying && (
                    <div className="text-center mt-2">
                        <button className="btn btn-outline-secondary py-3 px-3 button-create button-reset my-3" style={{ fontSize: '16px', color: '#fff1ea' }} onClick={handleReset}>
                            Reset Match
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}